/**
 * Service usageTracking — compteurs d'usage UI par utilisateur (tab
 * « Canvas » de l'onglet Beta users de /admin-datadog).
 *
 * Champs users/{uid} incrémentés côté client : télémétrie UI explicitement
 * NON FIABLE (issue #124), réservée aux diagnostics et à l'affichage local.
 * Elle n'alimente JAMAIS les statistiques publiques ni les tuiles officielles ;
 * celles-ci lisent serverRunCounts, écrit transactionnellement par le serveur.
 * Une callable par clic coûterait une invocation à chaque interaction.
 */

import { doc, updateDoc, increment, onSnapshot, Timestamp, Unsubscribe } from 'firebase/firestore';
import { auth, db } from './config';
import { BillingPlanId } from '../../types/billing';

export type UsageCounterField =
  // Clics sur le bouton « Reroll prompts » du dock (ToolsDock)
  | 'rerollCount'
  // Activations d'un switch « Auto run » (image ou vidéo, ON uniquement)
  | 'autorunActivationCount'
  // Générations relancées automatiquement par l'auto run (image ou vidéo)
  | 'autorunGenerationsCount'
  // Consultations de l'onglet Usage des Settings (/settings/usage)
  | 'usageTabViewsCount'
  // Runs lancés (clic sur Run, auto run et relances inclus — comptés à
  // l'enqueue, succès ou échec) : images, vidéos et compositions texte-only
  | 'runImagesCount'
  | 'runVideosCount'
  | 'runTextsCount';

/**
 * Incrémente un compteur d'usage sur le doc de l'utilisateur connecté.
 * Fire-and-forget : la télémétrie ne doit jamais bloquer ni faire échouer
 * l'action utilisateur. No-op si non connecté.
 */
export const trackUsageCounter = (field: UsageCounterField): void => {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  updateDoc(doc(db, 'users', uid), { [field]: increment(1) }).catch((error) => {
    console.warn(`[usageTracking] Failed to increment ${field}:`, error);
  });
};

// Consommation GPU réelle d'un utilisateur — champs users/{uid} écrits
// SERVEUR SEUL. Deux familles de compteurs :
// · SECONDES RÉELLES facturées (cold start inclus, transaction
//   recordSuccessfulGeneration de generateImage.js) — analytics coûts + FALLBACK des
//   jauges/enforcement pour les comptes legacy ;
// · UNITÉS FORFAITAIRES par run (quotaUnits*Used — recordSuccessfulGeneration pour les
//   générations, incrementBetaEventCounter de characters.js pour les
//   Rabbits) : jauges Settings → Usage (pourcentages de runs) et
//   enforcement. null quand le champ est absent (compte legacy jamais
//   migré → fallback secondes, PAS 0 qui effacerait l'historique).
// Fenêtrées (mensuelle / 6 heures — fenêtres DÉTERMINISTES ancrées sur
// createdAt : le compteur ne compte que si son début de fenêtre stocké
// correspond à la fenêtre courante calculée depuis createdAt, sinon la
// fenêtre a tourné = 0).
export interface GpuUsageSeconds {
  krea2Seconds: number;
  ltx25Seconds: number;
  totalSeconds: number;
  monthSeconds: number;
  monthStartAtMs: number;
  sixHourSeconds: number;
  sixHourStartAtMs: number;
  krea2Units: number | null;
  ltx25Units: number | null;
  totalUnits: number | null;
  monthUnits: number | null;
  sixHourUnits: number | null;
  // Compteurs Rabbits (users/{uid}, incrémentés serveur au LANCEMENT par
  // characters.js / characterDataset.js) — segment Rabbits de la jauge
  // « Total usage » (leur contribution en unités est déjà incluse dans
  // totalUnits / monthUnits / sixHourUnits côté serveur).
  loraTrainingsUsed: number;
  datasetGenerationsUsed: number;
  // Date d'inscription (users/{uid}.createdAt) — ancre des fenêtres.
  createdAtMs: number;
  // Unités bonus « Extra usage credits » (packs Stripe one-time) — champs
  // users/{uid} extraUnitsGrantedTotal / extraUnitsUsedTotal écrits SERVEUR
  // SEUL. null quand le champ est absent (compte legacy = 0) ; le solde
  // (granted − used) s'ajoute au plafond mensuel, la fenêtre 6 h est
  // inchangée.
  extraUnitsGrantedTotal: number | null;
  extraUnitsUsedTotal: number | null;
  // Abonnement Stripe (users/{uid}.subscription, écrit serveur par
  // stripeWebhook) : le plan actif/essai pilote les plafonds DÉRIVÉS EN LIVE
  // du tier adminConfig/usageTiers (resolveTierRunsForSubscription — source
  // unique « Define usage » ; users/{uid}.quotaLimits supprimé le 11/10).
  // null sinon.
  subscription: { planId: BillingPlanId | null; status: string } | null;
  // Ancre du cycle de facturation (ms — users/{uid}.quotaCycleStartMs,
  // écrite serveur à chaque paiement d'abonnement réussi) : la fenêtre
  // MENSUELLE des abonnés y est ancrée au lieu de createdAt. null pour les
  // comptes free/legacy.
  quotaCycleStartMs: number | null;
  // Runs texte du mode « Prompt » (users/{uid}.runTextsCount, incrémenté
  // client à l'enqueue) — segment « Prompts » de la jauge « Total usage »
  // (AFFICHAGE seul : les runs texte sont gratuits, hors quota GPU).
  promptRunsCount: number;
  // Compteur HEBDOMADAIRE (users/{uid}.quotaUnitsWeekUsed +
  // gpuWeekStartAtMs — fenêtre 7 jours tuilée depuis l'ancre, même règle
  // que les fenêtres mensuelle/6 h) : jauge « Weekly usage » des Settings —
  // part du budget MENSUEL consommée sur la semaine, la semaine n'a pas de
  // plafond propre (indicateur sans enforcement, null = champ absent).
  weekUnits: number | null;
  weekStartAtMs: number;
}

/**
 * Abonnement temps réel à la consommation GPU de l'utilisateur (0 tant que
 * les compteurs n'existent pas). L'appelant DOIT invoquer l'Unsubscribe
 * retourné (A21).
 */
export const subscribeMyGpuUsage = (
  uid: string,
  onData: (usage: GpuUsageSeconds) => void,
  onError?: (error: Error) => void
): Unsubscribe =>
  onSnapshot(
    doc(db, 'users', uid),
    (snap) => {
      const data = snap.data();
      const createdAt = data?.createdAt as Timestamp | undefined;
      // Unités : null quand le champ est absent (compte legacy) — le
      // fallback sur les secondes est résolu par les consommateurs.
      const unitsOrNull = (value: unknown): number | null =>
        typeof value === 'number' ? value : null;
      // Abonnement : planId + status suffisent à la dérivation des plafonds
      // (validation défensive — le doc est écrit serveur seul).
      const rawSub = data?.subscription;
      const subscription =
        rawSub && typeof rawSub === 'object'
          ? {
              planId:
                typeof rawSub.planId === 'string' ? (rawSub.planId as BillingPlanId) : null,
              status: typeof rawSub.status === 'string' ? rawSub.status : '',
            }
          : null;
      onData({
        krea2Seconds:
          typeof data?.gpuSecondsKrea2Used === 'number' ? data.gpuSecondsKrea2Used : 0,
        ltx25Seconds:
          typeof data?.gpuSecondsLtx25Used === 'number' ? data.gpuSecondsLtx25Used : 0,
        totalSeconds:
          typeof data?.gpuSecondsTotalUsed === 'number' ? data.gpuSecondsTotalUsed : 0,
        monthSeconds:
          typeof data?.gpuSecondsMonthUsed === 'number' ? data.gpuSecondsMonthUsed : 0,
        monthStartAtMs:
          typeof data?.gpuMonthStartAtMs === 'number' ? data.gpuMonthStartAtMs : 0,
        sixHourSeconds:
          typeof data?.gpuSeconds6hUsed === 'number' ? data.gpuSeconds6hUsed : 0,
        sixHourStartAtMs:
          typeof data?.gpu6hStartAtMs === 'number' ? data.gpu6hStartAtMs : 0,
        krea2Units: unitsOrNull(data?.quotaUnitsKrea2Used),
        ltx25Units: unitsOrNull(data?.quotaUnitsLtx25Used),
        totalUnits: unitsOrNull(data?.quotaUnitsTotalUsed),
        monthUnits: unitsOrNull(data?.quotaUnitsMonthUsed),
        sixHourUnits: unitsOrNull(data?.quotaUnits6hUsed),
        loraTrainingsUsed:
          typeof data?.loraTrainingsUsed === 'number' ? data.loraTrainingsUsed : 0,
        datasetGenerationsUsed:
          typeof data?.datasetGenerationsUsed === 'number' ? data.datasetGenerationsUsed : 0,
        createdAtMs: createdAt ? createdAt.toMillis() : 0,
        extraUnitsGrantedTotal: unitsOrNull(data?.extraUnitsGrantedTotal),
        extraUnitsUsedTotal: unitsOrNull(data?.extraUnitsUsedTotal),
        subscription,
        quotaCycleStartMs:
          typeof data?.quotaCycleStartMs === 'number' ? data.quotaCycleStartMs : null,
        promptRunsCount:
          typeof data?.runTextsCount === 'number' ? data.runTextsCount : 0,
        weekUnits: unitsOrNull(data?.quotaUnitsWeekUsed),
        weekStartAtMs:
          typeof data?.gpuWeekStartAtMs === 'number' ? data.gpuWeekStartAtMs : 0,
      });
    },
    onError
  );
